import i18next from 'i18next'

import { Maybe } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { mapCurrencyToLocales, useMoney } from '@/modules/shared/hooks/useMoney'

export interface MoneyItemProps {
  value: Maybe<number>
  currency?: string
  smartDecimals?: boolean // show 2 or 4 decimals based on the value
}

const DEFAULT_CURRENCY_CODE = 'USD'

export function MoneyItem({ value, currency, smartDecimals = false }: MoneyItemProps) {
  const { formatMoney, currency: sessionCurrency } = useMoney()
  const { currentOrganisation } = useSessionUser()

  if (!smartDecimals) {
    return <>{formatMoney({ amount: value, customCurrency: currency })}</>
  }

  const num = Number(value)
  if (isNaN(num)) {
    return <>{formatMoney({ amount: value, customCurrency: currency })}</>
  }

  const roundedToTwo = Math.round(num * 100) / 100
  const isTwoDecimals = Math.abs(num - roundedToTwo) < 0.00001

  let validCurrency = String(currency || sessionCurrency || DEFAULT_CURRENCY_CODE)
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: validCurrency })
  } catch {
    validCurrency = DEFAULT_CURRENCY_CODE
  }

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: validCurrency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: isTwoDecimals ? 2 : 4,
    maximumFractionDigits: isTwoDecimals ? 2 : 4,
  }

  let formatter: Intl.NumberFormat
  const locale = mapCurrencyToLocales(validCurrency, i18next.language)
  try {
    formatter = new Intl.NumberFormat(locale, options)
  } catch {
    formatter = new Intl.NumberFormat(locale, {
      ...options,
      currencyDisplay: 'symbol',
    })
  }

  let formatted = formatter.format(num)

  if (currentOrganisation?.featureFlags && !currentOrganisation?.featureFlags?.showCurrencySymbols) {
    const currencySymbol = formatter.formatToParts(0).find((part) => part.type === 'currency')?.value
    if (currencySymbol) {
      formatted = formatted.replace(currencySymbol, '').trim()
    }
  }

  if (validCurrency === 'AED' && !locale.startsWith('ar')) {
    const parts = formatted.replace('AED', '').trim()
    return (
      <>
        <span className="dirham-symbol">{'\u00EA'}</span> {parts}
      </>
    )
  }

  return <>{formatted}</>
}
